/**
 * /leads/new — a builder records a lead themselves (round11/portal-create-lead).
 *
 * The homeowner path stays the embed: they design, then submit. This page
 * is the other way round, for the customer who is already on the phone or
 * standing at the counter: the builder captures who they are and picks a
 * starting design — one of the tenant's own templates or a stock preset —
 * and the lead exists. The design is then reworked on the lead's own page
 * with the existing live editor, exactly like any homeowner's lead. That
 * two-step shape is deliberate: it reuses the one security-reviewed edit
 * mount instead of adding a third iframe surface here (see DEBT.md's
 * round11/portal-create-lead entry).
 *
 * Owner AND rep can create leads — reps take the calls. The server
 * (`POST /api/portal/leads`) is the authority on every rule below; the
 * client-side checks only save a round trip for the two required fields.
 *
 * One idempotency key per form, generated once on mount and reused on
 * retry, so a double-click or a flaky network never files two leads; a 409
 * (the retry landed after the first attempt already succeeded) is treated
 * as success and navigates to the lead.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PRESETS } from "@dockpro/core/catalogs";
import { useAuth } from "../contexts/auth-context";
import { ApiError, createLead, listTemplates, type TenantTemplate } from "../lib/api";
import { StatusMessage } from "../components/StatusMessage";
import { friendlyErrorMessage } from "../components/friendlyErrorMessage";
import ui from "../styles/ui.module.css";
import styles from "./NewLeadPage.module.css";

interface StartOption {
  key: string;
  label: string;
  description?: string;
  designState: unknown;
  group: "template" | "stock";
}

/** Server codes worth a sentence of their own; anything else falls through
 * to friendlyErrorMessage. Keys mirror validation.ts's validateLeadContact. */
const CREATE_ERROR_MESSAGES: Record<string, string> = {
  MISSING_CONTACT_NAME: "Enter the homeowner's name.",
  INVALID_CONTACT_EMAIL: "Enter a valid email address for the homeowner.",
  INVALID_CONTACT_PHONE: "Enter a phone number, or leave it blank.",
  INVALID_ADDRESS: "The address is too long (200 characters at most).",
  INVALID_CITY: "The city is too long (100 characters at most).",
  INVALID_ZIP: "The ZIP is too long (10 characters at most).",
  INVALID_MESSAGE: "Notes are too long (1,000 characters at most).",
  INVALID_DESIGN_STATE: "That starting design can't be loaded. Choose another one.",
};

function newIdempotencyKey(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === "function") return `portal-${c.randomUUID()}`;
  return `portal-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function stockOptions(): StartOption[] {
  const presets = PRESETS as unknown as Record<string, { name?: string; description?: string }>;
  return Object.entries(presets).map(([key, p]) => ({
    key: `stock:${key}`,
    label: p.name ?? key,
    description: p.description,
    designState: p,
    group: "stock",
  }));
}

export function NewLeadPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [idempotencyKey] = useState(newIdempotencyKey);

  const [templates, setTemplates] = useState<TenantTemplate[]>([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [notes, setNotes] = useState("");
  const [startKey, setStartKey] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The tenant's templates are the natural starting points; the stock
  // presets are always there behind them. A failed read is not an error the
  // builder can act on here — the stock list still makes the page work.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    listTemplates(user.idToken)
      // Defensive: a malformed response degrades to the stock designs
      // rather than a blank page (the axe scan's first fixture found this).
      .then((result) => { if (!cancelled) setTemplates(Array.isArray(result) ? result : []); })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) signOut();
      });
    return () => { cancelled = true; };
  }, [user, signOut]);

  // round12/builder-templates-only — the homeowner embed no longer offers
  // @dockpro/core's stock designs at all (Sarah, 2026-09-18). This page is
  // builder-facing, not homeowner-facing, and it CANNOT create a lead
  // without some starting design, so the stock list survives here as a
  // fallback ONLY while the builder has published nothing of their own. The
  // moment they author a template, their own designs are the only thing
  // offered here too — matching what their homeowners see.
  const options = useMemo<StartOption[]>(() => (
    templates.length > 0
      ? templates.map((t) => ({
          key: `template:${t.id}`, label: t.name, designState: t.designState, group: "template" as const,
        }))
      : stockOptions()
  ), [templates]);

  // Default to the first of the builder's own templates once they load,
  // otherwise the first stock design — without an effect, so a choice the
  // builder already made is never overwritten by a late template response.
  const effectiveKey = options.some((o) => o.key === startKey) ? startKey : (options[0]?.key ?? "");
  const selected = options.find((o) => o.key === effectiveKey);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user || isSaving) return;
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName) { setError("Enter the homeowner's name."); return; }
    if (!trimmedEmail) { setError("Enter the homeowner's email address."); return; }
    if (!selected) { setError("Choose a starting design."); return; }

    setIsSaving(true);
    setError(null);
    try {
      const created = await createLead(user.idToken, {
        idempotencyKey,
        contact: {
          name: trimmedName,
          email: trimmedEmail,
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          ...(address.trim() ? { address: address.trim() } : {}),
          ...(city.trim() ? { city: city.trim() } : {}),
          ...(zip.trim() ? { zip: zip.trim() } : {}),
          ...(notes.trim() ? { message: notes.trim() } : {}),
        },
        designState: selected.designState,
      });
      navigate(`/leads/${encodeURIComponent(created.idempotencyKey)}`);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) { signOut(); return; }
        if (err.status === 409) {
          // The first attempt already landed; this is its retry.
          navigate(`/leads/${encodeURIComponent(idempotencyKey)}`);
          return;
        }
        const known = err.code ? CREATE_ERROR_MESSAGES[err.code] : undefined;
        setError(known ?? friendlyErrorMessage(err, "create the lead"));
      } else {
        setError(friendlyErrorMessage(err, "create the lead"));
      }
    } finally {
      setIsSaving(false);
    }
  }

  if (!user) return null;

  const templateOptions = options.filter((o) => o.group === "template");
  const stock = options.filter((o) => o.group === "stock");

  return (
    <main id="main-content" className={`${ui.main} ${styles.main}`}>
      <div className={ui.pageHeader}>
        <div>
          <Link to="/leads" className={ui.backLink}>← Back to leads</Link>
          <h1>New lead</h1>
          <p className={ui.pageHint}>
            For a homeowner you're already talking to. Pick a starting design now; you can
            rework it on the lead's page once it exists.
          </p>
        </div>
      </div>

      {error && <StatusMessage variant="error" message={error} className={ui.error} />}

      <form onSubmit={handleSubmit} noValidate className={styles.form}>
        <section className={ui.card}>
          <div className={ui.cardHeader}>
            <h2 className={ui.cardTitle}>Homeowner</h2>
          </div>
          <div className={ui.fieldGrid}>
            <label className={ui.field}>
              <span>Name</span>
              <input type="text" required autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className={ui.field}>
              <span>Email</span>
              <input type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label className={ui.field}>
              <span>Phone</span>
              <input type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </label>
            <label className={ui.field}>
              <span>Address</span>
              <input type="text" autoComplete="street-address" maxLength={200} value={address} onChange={(e) => setAddress(e.target.value)} />
            </label>
            <label className={ui.field}>
              <span>City</span>
              <input type="text" autoComplete="address-level2" maxLength={100} value={city} onChange={(e) => setCity(e.target.value)} />
            </label>
            <label className={ui.field}>
              <span>ZIP</span>
              <input type="text" inputMode="numeric" autoComplete="postal-code" maxLength={10} value={zip} onChange={(e) => setZip(e.target.value)} />
            </label>
          </div>
          <label className={`${ui.field} ${ui.fieldSpaced}`}>
            <span>Notes</span>
            <textarea rows={3} maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
        </section>

        <section className={ui.card}>
          <div className={ui.cardHeader}>
            <h2 className={ui.cardTitle}>Starting design</h2>
          </div>
          <p className={ui.cardHint}>
            The lead opens with this dock on its page, ready to edit with the homeowner.
          </p>
          <label className={ui.field}>
            <span>Start from</span>
            <select value={effectiveKey} onChange={(e) => setStartKey(e.target.value)}>
              {templateOptions.length > 0 && (
                <optgroup label="Your templates">
                  {templateOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </optgroup>
              )}
              {stock.length > 0 && (
                <optgroup label="Stock designs">
                  {stock.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </optgroup>
              )}
            </select>
          </label>
          {stock.length > 0 && (
            <p className={ui.subtle}>
              These are DockPro starter designs. Homeowners on your site don&rsquo;t see
              them &mdash; they only ever start from{" "}
              <Link to="/templates">your templates</Link>, so it&rsquo;s worth saving a
              few.
            </p>
          )}
          {selected?.description && <p className={ui.subtle}>{selected.description}</p>}
        </section>

        <div className={ui.saveBar}>
          <button type="submit" className={ui.button} disabled={isSaving}>
            {isSaving ? "Creating…" : "Create lead"}
          </button>
          <Link to="/leads" className={ui.buttonSecondary}>Cancel</Link>
        </div>
      </form>
    </main>
  );
}
